import React from "react";
import { useTranslation } from "react-i18next";
import Section from "../components/Section";
import SectionHead from "../components/SectionHead";
import Button from "../components/Button";
import TaglineLine from "../components/TaglineLine";
import "./Home.css";

const Home: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero" id="home">
        <div className="hero-inner">
          <TaglineLine>
            <span>{t("hero.tagline.connect")}</span>
            <span>{t("hero.tagline.integrate")}</span>
            <span>{t("hero.tagline.perform")}</span>
          </TaglineLine>
          <h1 dangerouslySetInnerHTML={{ __html: t("hero.title") }} />
          <p className="hero-sub">{t("hero.subtitle")}</p>
          <div className="hero-actions">
            <Button variant="primary" href="#services">
              {t("hero.cta.explore")}
            </Button>
            <Button variant="secondary" href="#contact">
              {t("hero.cta.team")}
            </Button>
          </div>
        </div>
      </section>

      <div className="container">
        <div className="hero-meta">
          <div className="hero-meta-cell">
            <div className="lbl">{t("meta.headquarters")}</div>
            <div className="val">{t("meta.headquartersValue")}</div>
          </div>
          <div className="hero-meta-cell">
            <div className="lbl">{t("meta.coverage")}</div>
            <div className="val">{t("meta.coverageValue")}</div>
          </div>
          <div className="hero-meta-cell">
            <div className="lbl">{t("meta.serviceModels")}</div>
            <div className="val">{t("meta.serviceModelsValue")}</div>
          </div>
          <div className="hero-meta-cell">
            <div className="lbl">{t("meta.specialisation")}</div>
            <div className="val">{t("meta.specialisationValue")}</div>
          </div>
        </div>
      </div>

      {/* Brand Band */}
      <section className="brand-band">
        <div className="brand-band-inner">
          <div className="brand-word">
            <span className="num">{t("brand.connect.num")}</span>
            <span className="word">{t("brand.connect.title")}</span>
            <p className="desc">{t("brand.connect.desc")}</p>
          </div>
          <div className="brand-word">
            <span className="num">{t("brand.integrate.num")}</span>
            <span className="word">{t("brand.integrate.title")}</span>
            <p className="desc">{t("brand.integrate.desc")}</p>
          </div>
          <div className="brand-word">
            <span className="num">{t("brand.perform.num")}</span>
            <span className="word">{t("brand.perform.title")}</span>
            <p className="desc">{t("brand.perform.desc")}</p>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <Section>
        <div className="phil-split">
          <div className="phil-image">
            <img
              src="/img/photo-1486406146926-c627a92ad1ab.jpg"
              alt="Modern building facade representing intelligent infrastructure"
            />
            <div className="caption">{t("philosophy.caption")}</div>
          </div>
          <div>
            <SectionHead
              eyebrow={t("philosophy.eyebrow")}
              title={t("philosophy.title")}
            />
            <div className="phil-cards">
              <div className="phil-card">
                <span className="phil-num">01</span>
                <h3>{t("philosophy.card1.title")}</h3>
                <p>{t("philosophy.card1.desc")}</p>
              </div>
              <div className="phil-card">
                <span className="phil-num">02</span>
                <h3>{t("philosophy.card2.title")}</h3>
                <p>{t("philosophy.card2.desc")}</p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Services Section */}
      <Section alt id="services">
        <SectionHead
          eyebrow={t("services.eyebrow")}
          title={t("services.title")}
          description=""
        />
        <div className="pillars-grid">
          <a className="pillar-card" href="/service/energy-management">
            <div className="pillar-img">
              <img
                src="/img/photo-1509391366360-2e959784a276.jpg"
                alt="Solar panels and energy infrastructure"
              />
            </div>
            <div className="pillar-body">
              <div className="pillar-icon">
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                </svg>
              </div>
              <h3>{t("service.energy-management.title")}</h3>
              <p>{t("service.energy-management.desc")}</p>
              <span className="read-more">{t("readMore")}</span>
            </div>
          </a>
          <a className="pillar-card" href="/service/building-automation">
            <div className="pillar-img">
              <img
                src="https://images.unsplash.com/photo-1518709268805-4e9042af2176?auto=format&fit=crop&w=1600&q=80"
                alt="Modern building HVAC and automation infrastructure"
              />
            </div>
            <div className="pillar-body">
              <div className="pillar-icon">
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-4" />
                  <path d="M9 9v.01M9 12v.01M9 15v.01M9 18v.01" />
                </svg>
              </div>
              <h3>{t("service.building-automation.title")}</h3>
              <p>{t("service.building-automation.desc")}</p>
              <span className="read-more">{t("readMore")}</span>
            </div>
          </a>
          <a className="pillar-card" href="/service/smart-asset-maintenance">
            <div className="pillar-img">
              <img
                src="/img/photo-1581092335397-9583eb92d232.jpg"
                alt="Industrial maintenance technician inspecting equipment"
              />
            </div>
            <div className="pillar-body">
              <div className="pillar-icon">
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
              </div>
              <h3>{t("service.smart-asset-maintenance.title")}</h3>
              <p>{t("service.smart-asset-maintenance.desc")}</p>
              <span className="read-more">{t("readMore")}</span>
            </div>
          </a>
        </div>
      </Section>

      {/* CTA Section */}
      <section id="contact" className="cta">
        <div className="cta-inner">
          <TaglineLine className="cta-tagline">
            <span>{t("hero.tagline.connect")}</span>
            <span>{t("hero.tagline.integrate")}</span>
            <span>{t("hero.tagline.perform")}</span>
          </TaglineLine>
          <h2 dangerouslySetInnerHTML={{ __html: t("cta.title") }} />
          <p>{t("cta.desc")}</p>
          <Button
            variant="primary"
            href="mailto:tomislav@ametros.eu"
            target="_blank"
          >
            {t("cta.button")}
          </Button>
        </div>
      </section>

      {/* Solutions Section */}
      <Section id="solutions">
        <SectionHead
          eyebrow={t("solutions.eyebrow")}
          title={t("solutions.title")}
          description={t("solutions.desc")}
        />
        <div className="sol-layout">
          <div className="sol-image">
            <img
              src="/img/photo-1518770660439-4636190af475.jpg"
              alt="IoT sensors and connected hardware"
            />
          </div>
          <div className="sol-grid">
            <div className="sol">
              <span className="sol-tag">{t("solution.modular.tag")}</span>
              <h3>{t("solution.modular.title")}</h3>
              <p>{t("solution.modular.desc")}</p>
            </div>
            <div className="sol featured">
              <span className="sol-tag">{t("solution.saas.tag")}</span>
              <h3>{t("solution.saas.title")}</h3>
              <p>{t("solution.saas.desc")}</p>
            </div>
            <div className="sol">
              <span className="sol-tag">{t("solution.haas.tag")}</span>
              <h3>{t("solution.haas.title")}</h3>
              <p>{t("solution.haas.desc")}</p>
            </div>
          </div>
        </div>
      </Section>

      {/* Partners Section */}
      <Section id="partners">
        <SectionHead
          eyebrow={t("partners.eyebrow")}
          title={t("partners.title")}
          description={t("partners.desc")}
        />
        <div className="partners-split">
          <div className="partners-image">
            <img
              src="/img/photo-1517048676732-d65bc937f952.jpg"
              alt="Modern office building exterior representing enterprise partnerships"
            />
          </div>
          <div className="partners-content sol">
            <h3>{t("partners.single.title")}</h3>
            <p>{t("partners.single.p1")}</p>
            <ul>
              <li>{t("partners.single.smartThings")}</li>
              <li>{t("partners.single.biot")}</li>
            </ul>
            <p>{t("partners.single.enerbrain")}</p>
            <p>{t("partners.single.conclusion")}</p>
          </div>
        </div>
      </Section>

      {/* What We Deliver Section */}
      <Section>
        <SectionHead
          eyebrow={t("services.whatWeDeliver")}
          title={t("services.nineServices")}
          description={t("services.desc2")}
        />

        <div className="svc-banner">
          <img
            src="/img/photo-1581092160562-40aa08e78837.jpg"
            alt="Smart building control room with monitoring screens"
          />
          <div className="svc-banner-overlay">
            <div className="text">
              <h3>{t("services.banner.title")}</h3>
              <p>{t("services.banner.desc")}</p>
            </div>
          </div>
        </div>

        <div className="svc-grid">
          <a className="svc-card" href="/service/audit">
            <div className="svc-num">{t("service.audit.num")}</div>
            <h4>{t("service.audit.title")}</h4>
            <p>{t("service.audit.desc")}</p>
            <span className="read-more">{t("readMore")}</span>
          </a>
          <a className="svc-card" href="/service/advisory">
            <div className="svc-num">{t("service.advisory.num")}</div>
            <h4>{t("service.advisory.title")}</h4>
            <p>{t("service.advisory.desc")}</p>
            <span className="read-more">{t("readMore")}</span>
          </a>
          <a className="svc-card" href="/service/system-design">
            <div className="svc-num">{t("service.system-design.num")}</div>
            <h4>{t("service.system-design.title")}</h4>
            <p>{t("service.system-design.desc")}</p>
            <span className="read-more">{t("readMore")}</span>
          </a>
          <a className="svc-card" href="/service/capex">
            <div className="svc-num">{t("service.capex.num")}</div>
            <h4>{t("service.capex.title")}</h4>
            <p>{t("service.capex.desc")}</p>
            <span className="read-more">{t("readMore")}</span>
          </a>
          <a className="svc-card" href="/service/project-management">
            <div className="svc-num">{t("service.project-management.num")}</div>
            <h4>{t("service.project-management.title")}</h4>
            <p>{t("service.project-management.desc")}</p>
            <span className="read-more">{t("readMore")}</span>
          </a>
          <a className="svc-card" href="/service/system-integration">
            <div className="svc-num">{t("service.system-integration.num")}</div>
            <h4>{t("service.system-integration.title")}</h4>
            <p>{t("service.system-integration.desc")}</p>
            <span className="read-more">{t("readMore")}</span>
          </a>
          <a className="svc-card" href="/service/equipment-monitoring">
            <div className="svc-num">
              {t("service.equipment-monitoring.num")}
            </div>
            <h4>{t("service.equipment-monitoring.title")}</h4>
            <p>{t("service.equipment-monitoring.desc")}</p>
            <span className="read-more">{t("readMore")}</span>
          </a>
          <a className="svc-card" href="/service/measurement-verification">
            <div className="svc-num">
              {t("service.measurement-verification.num")}
            </div>
            <h4>{t("service.measurement-verification.title")}</h4>
            <p>{t("service.measurement-verification.desc")}</p>
            <span className="read-more">{t("readMore")}</span>
          </a>
          <a className="svc-card" href="/service/maintenance-services">
            <div className="svc-num">
              {t("service.maintenance-services.num")}
            </div>
            <h4>{t("service.maintenance-services.title")}</h4>
            <p>{t("service.maintenance-services.desc")}</p>
            <span className="read-more">{t("readMore")}</span>
          </a>
        </div>
      </Section>
    </div>
  );
};

export default Home;
